///
/// EarthBrowser
///
/// Created by Matt Giger on September 19, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Trial abuse analytics view: 30-day registration summary, high-risk
/// IP/device patterns (click a row for its full attempt history and any
/// accounts it created), recent blocked registrations, and per-day
/// registration stats. Fed by the pattern-detection tables the abuse
/// blocker already maintains; a weekly cron opens an Ops task when new
/// high-risk patterns appear.
///

import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useGetTrialAbuseAnalyticsQuery,
  useGetTrialAbuseDetailsQuery,
  type AbusePatternRow,
} from '@/redux/api/trialAbuseApi'
import { Fingerprint, Globe, ShieldAlert, UserX } from 'lucide-react'
import { useState } from 'react'

function riskColor(score: number): string {
  if (score >= 90) return 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/70'
  if (score >= 70) return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-500/70'
  return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/70'
}

function StatTile({ icon, label, value, sub, alert }: {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  alert?: boolean
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
      <div className={`p-2 rounded-lg ${alert
        ? 'bg-red-500/10 text-red-600 dark:text-red-400'
        : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-lg font-semibold leading-tight ${alert ? 'text-red-600 dark:text-red-400' : ''}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  )
}

// Full history for one flagged IP/device: every registration attempt and any
// accounts that were actually created from it.
function PatternDetailDialog({ pattern, onClose, onSelectUser }: {
  pattern: AbusePatternRow
  onClose: () => void
  onSelectUser: (userId: number) => void
}) {
  const { data, isLoading } = useGetTrialAbuseDetailsQuery({
    identifier: pattern.identifier,
    type: pattern.identifier_type,
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="rounded-xl max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-base">{pattern.identifier}</span>
            <Badge variant="outline" className="rounded-full uppercase text-[10px]">{pattern.identifier_type}</Badge>
            <Badge className={`rounded-full ${riskColor(pattern.risk_score)}`}>risk {pattern.risk_score}</Badge>
          </DialogTitle>
          <DialogDescription>
            {pattern.pattern_type} · {pattern.occurrence_count} occurrence(s) · last seen{' '}
            {new Date(pattern.last_seen_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <h4 className="text-sm font-semibold mb-2">Accounts created ({data?.users.length ?? 0})</h4>
              {(data?.users.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">No accounts were created from this {pattern.identifier_type}.</p>
              ) : (
                <div className="rounded-xl border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Converted</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data!.users.map(user => (
                        <TableRow
                          key={user.id}
                          className="cursor-pointer"
                          onClick={() => onSelectUser(user.id)}
                        >
                          <TableCell className="font-mono text-xs">{user.email}</TableCell>
                          <TableCell className="text-sm capitalize">{user.status}</TableCell>
                          <TableCell className="text-sm capitalize">{user.account_type}</TableCell>
                          <TableCell className="text-sm">{user.trial_converted ? 'Yes' : 'No'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2">Registration attempts ({data?.attempts.length ?? 0})</h4>
              <div className="rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.attempts.map((attempt, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{attempt.email}</TableCell>
                        <TableCell>
                          {attempt.blocked ? (
                            <Badge className="rounded-full bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/70">blocked</Badge>
                          ) : attempt.success ? (
                            <Badge className="rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/70">success</Badge>
                          ) : (
                            <Badge variant="outline" className="rounded-full">failed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {attempt.block_reason ?? attempt.failure_reason ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {new Date(attempt.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function TrialAbuseSection({ onSelectUser }: { onSelectUser: (userId: number) => void }) {
  const { data, isLoading, error } = useGetTrialAbuseAnalyticsQuery({ days: 30 })
  const [selected, setSelected] = useState<AbusePatternRow | null>(null)

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/70 bg-red-500/10 p-6 text-center">
        <p className="font-semibold text-red-600 dark:text-red-400">Error loading trial abuse analytics</p>
        <p className="text-sm mt-2 text-muted-foreground">Please check your permissions and try again.</p>
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-48 rounded-xl" />
      </div>
    )
  }

  const { summary, highRiskPatterns, recentBlocked, registrationStats } = data

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={<Globe className="h-4 w-4" />}
          label="Registration Attempts (30d)"
          value={summary.totalAttempts.toLocaleString()}
          sub={`${summary.successfulRegistrations} succeeded · ${summary.successRate ?? 0}% rate`}
        />
        <StatTile
          icon={<UserX className="h-4 w-4" />}
          label="Blocked (30d)"
          value={summary.blockedAttempts.toLocaleString()}
          sub={`${summary.blockRate ?? 0}% of attempts`}
          alert={summary.blockedAttempts > 0}
        />
        <StatTile
          icon={<ShieldAlert className="h-4 w-4" />}
          label="High-Risk Patterns"
          value={summary.highRiskPatterns.toLocaleString()}
          sub="risk score ≥ 70"
          alert={summary.highRiskPatterns > 0}
        />
        <StatTile
          icon={<Fingerprint className="h-4 w-4" />}
          label="Unique Sources (30d)"
          value={summary.uniqueIPs.toLocaleString()}
          sub={`IPs · ${summary.uniqueDevices} devices`}
        />
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">High-risk patterns</h3>
        {highRiskPatterns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center">
            <p className="text-sm text-muted-foreground">No patterns at or above risk score 50.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Identifier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Pattern</TableHead>
                  <TableHead>Occurrences</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Last Seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {highRiskPatterns.map((pattern) => (
                  <TableRow
                    key={`${pattern.identifier_type}:${pattern.identifier}`}
                    className="cursor-pointer"
                    onClick={() => setSelected(pattern)}
                  >
                    <TableCell className="font-mono text-xs">{pattern.identifier}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-full uppercase text-[10px]">
                        {pattern.identifier_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{pattern.pattern_type}</TableCell>
                    <TableCell className="text-sm">{pattern.occurrence_count}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full ${riskColor(pattern.risk_score)}`}>
                        {pattern.risk_score}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {new Date(pattern.last_seen_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Recently blocked registrations (7d)</h3>
        {recentBlocked.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center">
            <p className="text-sm text-muted-foreground">Nothing blocked in the last 7 days.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentBlocked.map((blocked, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{blocked.email}</TableCell>
                    <TableCell className="font-mono text-xs">{blocked.ip_address}</TableCell>
                    <TableCell className="text-sm">{blocked.block_reason}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {new Date(blocked.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {registrationStats.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Registrations by day</h3>
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Succeeded</TableHead>
                  <TableHead>Blocked</TableHead>
                  <TableHead>Unique IPs</TableHead>
                  <TableHead>Unique Emails</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrationStats.slice(0, 14).map((stats) => (
                  <TableRow key={stats.registration_date}>
                    <TableCell className="text-sm whitespace-nowrap">{stats.registration_date}</TableCell>
                    <TableCell className="text-sm">{stats.total_attempts}</TableCell>
                    <TableCell className="text-sm">{stats.successful}</TableCell>
                    <TableCell className={`text-sm ${stats.blocked > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                      {stats.blocked}
                    </TableCell>
                    <TableCell className="text-sm">{stats.unique_ips}</TableCell>
                    <TableCell className="text-sm">{stats.unique_emails}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {selected && (
        <PatternDetailDialog pattern={selected} onClose={() => setSelected(null)} onSelectUser={onSelectUser} />
      )}
    </div>
  )
}
